import type { ReactNode } from "react";
import { ApiError } from "@/lib/api-client";
import { useTenantStore } from "@/stores/tenant-store";
import { usePermissions } from "@/lib/permissions";
import type { Permission } from "@folio/shared/permissions";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import type { BankingIssueDto, BankingTotalsDto } from "../types";

export function BankingBoundary({
  permission,
  children,
}: {
  permission: Permission;
  children: ReactNode;
}) {
  const { activeTenantId, activeOrganizationId, organizations } =
    useTenantStore();
  const { hasPermission, isLoading } = usePermissions();
  if (isLoading) return <p>Cargando permisos…</p>;
  if (!hasPermission(permission))
    return <p role="alert">No tienes permiso para acceder a esta sección.</p>;
  if (
    !activeTenantId ||
    !activeOrganizationId ||
    organizations[activeOrganizationId]?.tenantId !== activeTenantId
  )
    return (
      <p role="alert">Selecciona una organización autorizada para continuar.</p>
    );
  return (
    <div
      key={`${activeTenantId}:${activeOrganizationId}`}
      className="space-y-6"
    >
      {children}
    </div>
  );
}
export function BankingPanel({
  title,
  children,
}: {
  title: string;
  children: ReactNode;
}) {
  return (
    <Card className="space-y-4 p-5">
      <h2 className="text-lg font-semibold">{title}</h2>
      {children}
    </Card>
  );
}
export function BankingIssues({ issues }: { issues: BankingIssueDto[] }) {
  if (!issues.length) return null;
  return (
    <ul className="space-y-1 text-sm text-destructive">
      {issues.map((issue, index) => (
        <li key={index}>
          Fila {issue.sourceRow ?? "archivo"} · {issue.field ?? "estructura"} ·{" "}
          {issue.code}: {issue.cause}
        </li>
      ))}
    </ul>
  );
}
export function BankingError({ error }: { error: Error | null }) {
  if (!error) return null;
  const data =
    error instanceof ApiError &&
    typeof error.data === "object" &&
    error.data !== null
      ? (error.data as {
          code?: string;
          correlationId?: string;
          details?: { issues?: BankingIssueDto[] };
        })
      : null;
  return (
    <div
      role="alert"
      className="rounded border border-destructive p-3 text-destructive"
    >
      <p>
        {error.message}
        {data?.code && ` (${data.code})`}
      </p>
      {error instanceof ApiError && error.status === 409 && (
        <p>
          El estado cambió. Recarga los datos; si la vista previa está
          desactualizada, actualízala y vuelve a decidir los duplicados.
        </p>
      )}
      {data?.correlationId && <p>Referencia: {data.correlationId}</p>}
      {data?.details?.issues && <BankingIssues issues={data.details.issues} />}
    </div>
  );
}
export function BankingTotals({
  label,
  totals,
  currency,
}: {
  label: string;
  totals: BankingTotalsDto;
  currency: string;
}) {
  return (
    <p>
      {label}: Cargos {totals.debit} · Abonos {totals.credit} {currency}
    </p>
  );
}
export function BankingPager({
  next,
  pending,
  onNext,
  onReset,
}: {
  next: string | null | undefined;
  pending: boolean;
  onNext: () => void;
  onReset: () => void;
}) {
  return (
    <div className="flex gap-2">
      <Button variant="outline" disabled={pending} onClick={onReset}>
        Primera página / Actualizar
      </Button>
      <Button variant="outline" disabled={pending || !next} onClick={onNext}>
        Siguiente página
      </Button>
    </div>
  );
}
